import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import type { FieldTelemetryConfig, FieldTelemetryData } from '@indeed/jspl-questions';
import type {
    ScreenerQuestionContextQuestion,
    SmartApplyScreenerQuestionType,
} from '../../../__generated__/graphql-base-types';
import type { EventLogger } from '../../../lib/eventLogger/eventLogger.client';
import { getDisplayValues, isAnswerable, isAnswered } from './screenerQuestionUtils';
import type { AnswerValue } from './screenerQuestionUtils';

/**
 * Question types whose jspl component reports a freeform `answerCharacterCount` on answer
 * (`TextQuestion`, `RichTextQuestion`, `NumberQuestion`, `DateQuestion` — `ScreenerQuestionField`
 * renders TEXT through `NumberQuestion` for a numeric `format`, but the GraphQL `type` stays TEXT,
 * so this set does not need to read `format`). Every other answerable type reports `answerLength`
 * instead (a selection or file count) — see `SingleSelectQuestion`/`MultiSelectQuestion`/
 * `HierarchicalSelectQuestion`/`FileUploadQuestion`.
 */
const CHARACTER_COUNT_TYPES = new Set<SmartApplyScreenerQuestionType>(['TEXT', 'TEXTAREA', 'DATE']);

/**
 * The prefill answer metric jspl's `FieldTelemetryProvider` expects to seed a `PREFILL` row —
 * mirrors what the field itself reports `onAnswer` once mounted, so a `PREFILL` and a later `USER`
 * row for the same question are comparable.
 */
const prefillMetric = (
    question: ScreenerQuestionContextQuestion,
    loadedAnswers: Record<string, AnswerValue>,
): Pick<FieldTelemetryConfig, 'answerCharacterCount' | 'answerLength'> => {
    const displayValues = getDisplayValues(question, loadedAnswers[question.name]);
    return CHARACTER_COUNT_TYPES.has(question.type)
        ? { answerCharacterCount: (displayValues[0] ?? '').trim().length }
        : { answerLength: displayValues.length };
};

/**
 * The jspl field-telemetry config to register for this page's questions, keyed the same way as the
 * rest of the page's state — `question.name`, the id jspl's question components are given as their
 * `name` prop (see `ScreenerQuestionField`'s `shared.name`).
 *
 * Excludes `PAGEBREAK`/`INFORMATION` (no answer, no jspl field) and `MULTIFIELD` (renders read-only
 * pending the entry editor, IAFE-2910 — a config row for it would be permanently inert) by reusing
 * `isAnswerable`, the page's own definition of "this question type has a jspl field to track".
 *
 * A question already answered on load (prefill or a previously saved draft answer — `loadedAnswers`
 * makes no distinction, matching the MPMs) seeds an `answerSource: 'PREFILL'` row; jspl replaces it
 * with an `answerSource: 'USER'` row once the jobseeker edits it, so such a question emits two rows.
 */
export const buildQuestionTelemetryConfig = (
    questions: ScreenerQuestionContextQuestion[],
    loadedAnswers: Record<string, AnswerValue>,
): FieldTelemetryConfig[] =>
    questions
        .filter((question) => isAnswerable(question.type))
        .map((question) => ({
            id: question.name,
            type: question.type,
            required: question.required,
            ...(isAnswered(question, loadedAnswers[question.name])
                ? { answerSource: 'PREFILL', ...prefillMetric(question, loadedAnswers) }
                : {}),
        }));

/**
 * A stable identity for one telemetry row, for idempotent retry across flush attempts.
 * Includes `errorVisible` because an unanswered question has no `answerOrder` to bump when it
 * goes from no-error to error-shown, so without it that transition would look like a duplicate.
 */
export const questionInteractionRowKey = (
    item: Pick<FieldTelemetryData, 'id' | 'answerSource' | 'answerOrder'> &
        Partial<Pick<FieldTelemetryData, 'errorVisible'>>,
): string =>
    JSON.stringify([item.id, item.answerSource, item.answerOrder, item.errorVisible ?? false]);

export interface EmitQuestionInteractionsParams {
    eventLogger: EventLogger;
    /** One `IaQuestionInteraction` row per entry, from `useFieldTelemetryData`'s `data`. */
    data: FieldTelemetryData[];
    /** Resolves each row's `id` (a `question.name`) back to the question's persistent `id`. */
    questions: ScreenerQuestionContextQuestion[];
    iaUid: string | undefined;
    draftId: string | undefined;
    /** `'auto-apply-questions'` on the main spine, `'auto-apply-questions-edit'` on the side-trip. */
    page: string;
    /** Row keys (`questionInteractionRowKey`) a previous call already got a `true` result for. */
    alreadyAcceptedKeys: ReadonlySet<string>;
}

export interface EmitQuestionInteractionsResult {
    /** Row keys the logger accepted (returned `true`) this call — merge into the caller's set. */
    acceptedKeys: string[];
    /**
     * True once every row in `data` is accounted for as accepted, including ones skipped because
     * `alreadyAcceptedKeys` already covered them. The caller should only call `markAsLogged()` when
     * this is true — otherwise an unaccepted row would be dropped for good on the next flush.
     */
    allAccepted: boolean;
}

/**
 * Emits one `IaQuestionInteraction` Signals event per not-yet-accepted `useFieldTelemetryData` row.
 * Never throws — telemetry must not change product behaviour — but unlike this page's other
 * emitters (`emitScreenerFieldEdits`, `ScreenerQuestionITV`, which rely on their caller's try/catch)
 * this one reports success per row via `eventLogger.logQuestionInteraction`'s boolean return, so a
 * transient transport failure can be retried on the next flush instead of being silently dropped by
 * `markAsLogged()`.
 */
export const emitQuestionInteractions = ({
    eventLogger,
    data,
    questions,
    iaUid,
    draftId,
    page,
    alreadyAcceptedKeys,
}: EmitQuestionInteractionsParams): EmitQuestionInteractionsResult => {
    // jspl tracks fields by `question.name` (see buildQuestionTelemetryConfig); the funnel wants the
    // question's persistent `id`, matching `emitScreenerFieldEdits`'s `fieldName`/`questionId`.
    const idByName = new Map(questions.map((question) => [question.name, question.id]));

    const acceptedKeys: string[] = [];
    let allAccepted = true;

    data.forEach((item) => {
        const key = questionInteractionRowKey(item);
        if (alreadyAcceptedKeys.has(key)) return;

        try {
            const accepted = eventLogger.logQuestionInteraction?.({
                iaUid: AvroOptional.string(iaUid),
                applicationDraftId: AvroOptional.string(draftId),
                page: AvroOptional.string(page),
                questionId: idByName.get(item.id) ?? item.id,
                questionType: item.type,
                required: item.required,
                questionOrder: item.order,
                answerCharacterCount: AvroOptional.int(item.answerCharacterCount),
                answerLength: AvroOptional.int(item.answerLength),
                answerOrder: AvroOptional.int(item.answerOrder),
                answerSource: AvroOptional.string(item.answerSource),
                viewed: item.viewed,
                timeSpent: item.timeSpent,
                focusTime: item.focusTime,
                dirty: item.dirty,
                touched: item.touched,
                errorMessage: AvroOptional.string(item.errorMessage),
                errorVisible: item.errorVisible,
            });
            if (accepted) acceptedKeys.push(key);
            else allAccepted = false;
        } catch {
            allAccepted = false;
        }
    });

    return { acceptedKeys, allAccepted };
};
